import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import BrandMark from '../components/BrandMark';
import './login.css';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

const LAST_COMPANY_KEY = 'bbqsync_last_company';
// Πρόσφατα καταστήματα ΜΟΝΟ αυτής της συσκευής (καμία λίστα πελατών από τον server)
const RECENT_COMPANIES_KEY = 'estiasync_recent_companies';

const readRecents = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(RECENT_COMPANIES_KEY) || '[]');
    return Array.isArray(arr) ? arr.filter(c => typeof c === 'string' && c.trim()) : [];
  } catch { return []; }
};

const saveRecent = (name) => {
  const c = String(name || '').trim().toLowerCase();
  if (!c) return;
  try {
    const next = [c, ...readRecents().filter(x => x !== c)].slice(0, 5);
    localStorage.setItem(RECENT_COMPANIES_KEY, JSON.stringify(next));
    localStorage.setItem(LAST_COMPANY_KEY, c);
  } catch {}
};

// Σπίθες που «πετάγονται» περιστασιακά από τους κύκλους του background,
// σε ΡΙΠΕΣ: κάθε σημείο εκπομπής (πάνω στα τόξα/δαχτυλίδια) εκτοξεύει μια
// βεντάλια από σπίθες σχεδόν ταυτόχρονα, όπως το πιτσίλισμα ενός κάρβουνου.
// Κάθε σπίθα είναι μικρή και διάφανη, ώστε το πλήθος να μην κουράζει το μάτι.
// Σπίθες ψησταριάς με πραγματική τυχαιότητα (Web Animations API):
// κάθε 3-5" περίπου, ένας ΤΥΧΑΙΟΣ κύκλος του background (ποτέ ο ίδιος
// δύο συνεχόμενες φορές) «σκάει» μια ριπή με τυχαίο πλήθος σπιθών.
// Κάθε σπίθα ακολουθεί βαλλιστική τροχιά με βαρύτητα και τρεμοσβήνει
// τυχαία — καμία ριπή δεν είναι ίδια με άλλη, όπως σε αληθινά κάρβουνα.
const EMBER_EMITTERS = [
  { fx: 0.23, fy: 0.14 },  // πάνω-αριστερά τόξα
  { fx: 0.88, fy: 0.82 },  // κάτω-δεξιά τόξα
  { fx: 0.95, fy: 0.24 },  // δεξί μικρό τόξο
  { fx: 0.10, fy: 0.31 },  // δαχτυλίδι πάνω-αριστερά
  { fx: 0.91, fy: 0.67 },  // δαχτυλίδι κάτω-δεξιά
  { fx: 0.06, fy: 0.85 },  // αριστερό κάτω τόξο
];
const EMBER_FIRE  = ['#fde047', '#fbbf24', '#fb923c', '#ef4444'];
const EMBER_BRAND = ['#0ea5e9', '#06b6d4', '#10b981'];
// Παλέτα ηλεκτροκόλλησης: λευκοπύρωτο κέντρο προς κίτρινο/πορτοκαλί
const EMBER_WELD  = ['#fffbe6', '#fff3b0', '#fde047', '#fbbf24', '#fb923c'];

function useEmberBursts(bgRef, cardRef, screenRef) {
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    const bg = bgRef.current;
    const card = cardRef.current;
    const screen = screenRef.current;
    if (!bg || typeof bg.animate !== 'function') return; // χωρίς WAAPI: απλά χωρίς σπίθες

    const timers = new Set();
    const later = (fn, ms) => {
      const t = setTimeout(() => { timers.delete(t); fn(); }, ms);
      timers.add(t);
    };
    let lastEmitter = -1;

    /**
     * Εκτοξεύει σπίθες από το (x0, y0) μέσα στο host.
     * o: { count, sizeMin/Max, speedMin/Max, g, tMin/Max, opMax, fireBias }
     */
    const spawnSparks = (host, x0, y0, o) => {
      for (let i = 0; i < o.count; i++) {
        const sp = document.createElement('span');
        sp.className = 'loginEmber';
        const size = o.sizeMin + Math.random() * (o.sizeMax - o.sizeMin);
        const pal = Math.random() < o.fireBias ? EMBER_FIRE : EMBER_BRAND;
        const c = pal[Math.floor(Math.random() * pal.length)];
        sp.style.width = sp.style.height = `${size.toFixed(1)}px`;
        sp.style.background = c;
        sp.style.boxShadow = `0 0 ${Math.max(4, size * 2).toFixed(0)}px ${c}`;
        sp.style.left = `${x0.toFixed(0)}px`;
        sp.style.top = `${y0.toFixed(0)}px`;
        host.appendChild(sp);

        // βαλλιστική: τυχαία γωνία γύρω από την κατακόρυφο, τυχαία ορμή,
        // βαρύτητα που τη γυρίζει προς τα κάτω
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.9;
        const speed = o.speedMin + Math.random() * (o.speedMax - o.speedMin); // px/s
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;
        const g = o.g * (0.85 + Math.random() * 0.3);        // px/s²
        const T = o.tMin + Math.random() * (o.tMax - o.tMin); // διάρκεια ζωής (s)

        const steps = 9;
        const frames = [];
        for (let s = 0; s <= steps; s++) {
          const t = (s / steps) * T;
          frames.push({
            transform: `translate(${(vx * t).toFixed(1)}px, ${(vy * t + 0.5 * g * t * t).toFixed(1)}px) scale(${(1 - 0.55 * s / steps).toFixed(2)})`,
            // τυχαίο τρεμοσβήσιμο, αναμμένη στην εκτόξευση, σβήνει στο τέλος
            opacity: s === 0 || s === steps ? 0
              : s === 1 ? o.opMax
              : 0.15 + Math.random() * (o.opMax - 0.15),
          });
        }
        const delayMs = Math.random() * 220; // όχι όλες στο ίδιο ms
        sp.animate(frames, { duration: T * 1000, delay: delayMs, easing: 'linear', fill: 'both' });
        // Αφαίρεση με timer και ΟΧΙ με onfinish: σε ορισμένα περιβάλλοντα
        // Chrome το finish event των WAAPI animations δεν πυροδοτείται ποτέ
        // και οι «τελειωμένες» σπίθες συσσωρεύονταν στο DOM επ' άπειρον
        later(() => sp.remove(), delayMs + T * 1000 + 120);
      }
    };

    // Ριπές από τους κύκλους του background: τυχαίος πομπός κάθε ~3-5",
    // ποτέ ο ίδιος δύο συνεχόμενες φορές
    const burstLoop = () => {
      const w = bg.clientWidth, h = bg.clientHeight;
      if (w && h) {
        let ei;
        do { ei = Math.floor(Math.random() * EMBER_EMITTERS.length); } while (ei === lastEmitter);
        lastEmitter = ei;
        spawnSparks(bg, EMBER_EMITTERS[ei].fx * w, EMBER_EMITTERS[ei].fy * h, {
          count: 4 + Math.floor(Math.random() * 6),   // 4-9 σπίθες
          sizeMin: 2, sizeMax: 4.5,
          speedMin: 55, speedMax: 150,
          g: 175, tMin: 1.6, tMax: 3.1,
          opMax: 0.55, fireBias: 0.55,
        });
      }
      later(burstLoop, 3500 + Math.random() * 3500);  // κάθε ~3.5-7"
    };

    // Μικρο-σπίθες από τη φλόγα του εικονιδίου: πετάγονται ελαφρά προς τα
    // πάνω και ΠΕΦΤΟΥΝ με βαρύτητα πάνω στα input boxes — προσγειώνονται στο
    // πραγματικό πάνω χείλος του πεδίου, αναπηδούν ανεπαίσθητα και σβήνουν
    // εκεί, σαν να ισχύουν οι φυσικοί νόμοι μέσα στην κάρτα
    // Επιφάνειες της κάρτας που «πιάνουν» σπίθες: κείμενα (μέσω inline spans
    // με τα πραγματικά όρια των γραμμάτων), chip εταιρείας, πεδία, κουμπί, link
    const SURFACE_SEL = '.loginTitle > span, .loginSubtitle > span, .loginCompany, ' +
      '.loginSwitchPanel, .loginAlert, .loginInput, .loginSubmit, .loginFooter a';

    /**
     * Ρίχνει σπίθες κόλλησης από τη φλόγα του εικονιδίου στο full-screen
     * στρώμα. intense=false: το συνεχές «τρυπάνι» (λίγες, απαλές).
     * intense=true: έξαρση — πολλές, λευκοπύρωτες, με φλας τόξου.
     */
    const fireWeld = (intense) => {
      const img = card?.querySelector('.brand-mark__icon');
      // Full-screen στρώμα: οι σπίθες ξεχύνονται σε ΟΛΗ την οθόνη, πάνω από
      // την κάρτα — η φυσική πρόσκρουσης στο περιεχόμενό της παραμένει
      const overlay = screen;
      if (img && overlay) {
        const ir = img.getBoundingClientRect();
        const or = overlay.getBoundingClientRect();
        // ό,τι βρίσκεται κάτω από τη φλόγα, ταξινομημένο από πάνω προς τα κάτω
        const surfaces = [...card.querySelectorAll(SURFACE_SEL)]
          .map((el) => el.getBoundingClientRect())
          .map((r) => ({ top: r.top - or.top, left: r.left - or.left, right: r.right - or.left }))
          .sort((a, b) => a.top - b.top);

        // Το «τόξο» της κόλλησης: εκτυφλωτικό φλας μόνο στις εξάρσεις
        if (intense) {
          const fx = ir.left - or.left + ir.width * 0.5;
          const fy = ir.top - or.top + ir.height * 0.28;
          const flash = document.createElement('span');
          flash.className = 'loginWeldFlash';
          flash.style.left = `${(fx - 9).toFixed(0)}px`;
          flash.style.top = `${(fy - 9).toFixed(0)}px`;
          overlay.appendChild(flash);
          const flashDur = 650 + Math.random() * 500;
          flash.animate(
            Array.from({ length: 14 }, (_, s) => ({
              opacity: s === 0 || s === 13 ? 0 : 0.25 + Math.random() * 0.75,
              transform: `scale(${(0.7 + Math.random() * 0.7).toFixed(2)})`,
            })),
            { duration: flashDur, easing: 'linear', fill: 'both' }
          );
          later(() => flash.remove(), flashDur + 100);
        }

        const count = intense
          ? 25 + Math.floor(Math.random() * 31)  // έξαρση: 25-55 σπίθες
          : 1 + Math.floor(Math.random() * 2);   // τρυπάνι: 1-2
        const delaySpread = intense ? 1100 : 260;
        for (let i = 0; i < count; i++) {
          // οι σπίθες ξεπηδούν από το σημείο του τόξου
          const x0 = ir.left - or.left + ir.width * (0.42 + Math.random() * 0.16);
          const y0 = ir.top - or.top + ir.height * 0.28;

          // βίαιη βαλλιστική ηλεκτροκόλλησης — στο τρυπάνι πιο συμμαζεμένη
          const u = intense ? 30 + Math.random() * 110 : 15 + Math.random() * 60;
          const vx = (Math.random() - 0.5) * 2 * (intense ? 40 + Math.random() * 220 : 20 + Math.random() * 120);
          const g = 300 + Math.random() * 200;                  // px/s²

          // Πρώτη επιφάνεια που συναντά η τροχιά: ελέγχουμε πού βρίσκεται
          // οριζόντια η σπίθα τη στιγμή που περνά το πάνω χείλος καθεμιάς
          let dy = null, tLand = null;
          for (const sf of surfaces) {
            if (sf.top <= y0 + 6) continue;
            const dyS = sf.top - y0 - 2;
            const t = (u + Math.sqrt(u * u + 2 * g * dyS)) / g;
            const x = x0 + vx * t;
            if (x >= sf.left - 1 && x <= sf.right + 1) { dy = dyS; tLand = t; break; }
          }
          if (dy === null) {
            // δεν βρήκε τίποτα μπροστά της: πέφτει ως το κάτω μέρος της κάρτας
            dy = or.height - y0 - 3;
            tLand = (u + Math.sqrt(u * u + 2 * g * dy)) / g;
          }

          // σπίθα ηλεκτροκόλλησης: λεπτή φωτεινή ΓΡΑΜΜΗ που ευθυγραμμίζεται
          // με την ταχύτητά της σε κάθε στιγμή (στρέφεται όσο καμπυλώνει)
          const sp = document.createElement('span');
          sp.className = 'loginEmber loginWeldSpark';
          // Στην έξαρση μεγάλη ποικιλία μεγεθών: από ψιλή σκόνη ως χοντρές
          const len = intense ? 2 + Math.random() * 12 : 2 + Math.random() * 4;
          const thick = intense ? 0.8 + Math.random() * 1.4 : 0.8 + Math.random() * 0.6;
          // Έξαρση: λευκοπύρωτα, εκτυφλωτικά χρώματα κόλλησης.
          // Τρυπάνι: πιο ήπιες αποχρώσεις φωτιάς/brand, σαν το background.
          const cPal = intense
            ? (Math.random() < 0.8 ? EMBER_WELD : [...EMBER_BRAND, '#ef4444'])
            : (Math.random() < 0.7 ? EMBER_FIRE : EMBER_BRAND);
          const c = cPal[Math.floor(Math.random() * cPal.length)];
          sp.style.width = `${len.toFixed(1)}px`;
          sp.style.height = `${thick.toFixed(1)}px`;
          // Φωτεινή «κεφαλή» μπροστά, ουρά που αραιώνει γρήγορα προς τα πίσω —
          // όχι ομοιόμορφη γραμμούλα (feedback 8/8/2026)
          sp.style.background = `linear-gradient(to right, transparent, ${c}44 45%, ${c} 78%, #fff)`;
          sp.style.boxShadow = intense ? `0 0 3px ${c}` : `0 0 2px ${c}`;
          sp.style.left = `${x0.toFixed(0)}px`;
          sp.style.top = `${y0.toFixed(0)}px`;
          overlay.appendChild(sp);

          // Πτώση ως το εμπόδιο. Στο τρυπάνι σβήνει αμέσως στην πρόσκρουση·
          // στην έξαρση η καύτρα ΑΝΤΕΧΕΙ πάνω στην επιφάνεια: μένει εκεί
          // 0.5-1.6" σαν χόβολη που τρεμοσβήνει καθώς κρυώνει
          const restT = intense ? 0.22 + Math.random() * 0.45 : 0.14; // έξαρση: σβήνουν σε 0.22-0.67" (ήταν 0.5-1.6")
          const TT = tLand + restT;
          const rotAt = (t) => (Math.atan2(-u + g * t, vx) * 180 / Math.PI).toFixed(1);
          const frames = [];
          const flightSteps = 8;
          const lenPx = `${len.toFixed(1)}px`;
          const dotPx = `${Math.max(1.4, thick).toFixed(1)}px`; // στην προσγείωση: κουκκίδα-καύτρα, όχι γραμμή
          for (let s = 0; s <= flightSteps; s++) {
            const t = (s / flightSteps) * tLand;
            frames.push({
              offset: t / TT,
              transform: `translate(${(vx * t).toFixed(1)}px, ${(-u * t + 0.5 * g * t * t).toFixed(1)}px) rotate(${rotAt(t)}deg)`,
              width: lenPx,
              // φωτεινή σε όλη την πτήση, με ζωντανό τρεμοσβήσιμο —
              // στο τρυπάνι πιο διακριτική
              opacity: s === 0 ? 0
                : intense ? 0.65 + Math.random() * 0.35
                : 0.3 + Math.random() * 0.3,
            });
          }
          const landTf = `translate(${(vx * tLand).toFixed(1)}px, ${dy.toFixed(1)}px) rotate(${rotAt(tLand)}deg)`;
          frames.push({ offset: tLand / TT, transform: landTf, width: dotPx, opacity: intense ? 0.95 : 0.55 });
          if (intense) {
            // χόβολη πάνω στην επιφάνεια: άτακτο τρεμοσβήσιμο μέχρι να κρυώσει
            frames.push({ offset: (tLand + restT * 0.3) / TT, transform: landTf, width: dotPx, opacity: 0.35 + Math.random() * 0.25 });
            frames.push({ offset: (tLand + restT * 0.55) / TT, transform: landTf, width: dotPx, opacity: 0.5 + Math.random() * 0.3 });
            frames.push({ offset: (tLand + restT * 0.8) / TT, transform: landTf, width: dotPx, opacity: 0.2 + Math.random() * 0.2 });
          }
          frames.push({ offset: 1, transform: landTf, width: dotPx, opacity: 0 });

          const delayMs = Math.random() * delaySpread; // απλωμένη «βροχή», όχι όλες στο ίδιο ms
          sp.animate(frames, { duration: TT * 1000, delay: delayMs, easing: 'linear', fill: 'both' });
          later(() => sp.remove(), delayMs + TT * 1000 + 120);

          // Σκόρπισμα στην πρόσκρουση: 2-4 μικρο-θραύσματα τινάζονται πλάγια
          // από το σημείο επαφής, με δική τους βαλλιστική, και σβήνουν γρήγορα
          const xLand = x0 + vx * tLand;
          const yLand = y0 + dy;
          later(() => {
            const n = intense ? 2 + Math.floor(Math.random() * 3) : 1 + Math.floor(Math.random() * 2);
            for (let k = 0; k < n; k++) {
              const f = document.createElement('span');
              f.className = 'loginEmber';
              const fs = 1 + Math.random() * 0.9;
              const fPal = Math.random() < 0.65 ? EMBER_FIRE : EMBER_BRAND;
              const fc = fPal[Math.floor(Math.random() * fPal.length)];
              f.style.width = f.style.height = `${fs.toFixed(1)}px`;
              f.style.background = fc;
              f.style.boxShadow = `0 0 5px ${fc}`;
              f.style.left = `${xLand.toFixed(0)}px`;
              f.style.top = `${(yLand - 1).toFixed(0)}px`;
              overlay.appendChild(f);

              const v2 = (Math.random() < 0.5 ? -1 : 1) * (15 + Math.random() * 70);
              const u2 = 5 + Math.random() * 32;
              const g2 = 300 + Math.random() * 120;
              const T2 = 0.35 + Math.random() * 0.5;
              const fFrames = [];
              const fSteps = 5;
              for (let s = 0; s <= fSteps; s++) {
                const t = (s / fSteps) * T2;
                fFrames.push({
                  transform: `translate(${(v2 * t).toFixed(1)}px, ${(-u2 * t + 0.5 * g2 * t * t).toFixed(1)}px) scale(${(1 - 0.5 * s / fSteps).toFixed(2)})`,
                  opacity: s === 0 ? 0.8 : s === fSteps ? 0 : 0.3 + Math.random() * 0.45,
                });
              }
              f.animate(fFrames, { duration: T2 * 1000, easing: 'linear', fill: 'both' });
              later(() => f.remove(), T2 * 1000 + 100);
            }
          }, delayMs + tLand * 1000);
        }
      }
    };

    // Η ροή της κόλλησης, ισορροπημένη (8/8/2026): «τρυπάνι» (1-2 σπίθες κάθε
    // 0.5-1.5"), πότε-πότε ΔΙΑΛΕΙΜΜΑ 2.5-4.5" — και μετά από διάλειμμα, συχνά,
    // μια έξαρση (όπως ο κολλητής που ξανακουμπά το ηλεκτρόδιο)
    const flowLoop = () => {
      if (Math.random() < 0.35) {
        later(() => {
          if (Math.random() < 0.45) fireWeld(true);
          later(flowLoop, 300);
        }, 2500 + Math.random() * 2000); // διάλειμμα 2.5-4.5"
      } else {
        fireWeld(false);
        later(flowLoop, 500 + Math.random() * 1000);
      }
    };

    // Άμεσο ξεκίνημα: μία έξαρση μόλις φορτώσει η σελίδα (όχι κενή αναμονή),
    // και οι δύο βρόχοι ξεκινούν σχεδόν αμέσως
    later(() => fireWeld(true), 350);
    later(burstLoop, 400 + Math.random() * 400);
    later(flowLoop, 900 + Math.random() * 500);
    return () => {
      timers.forEach(clearTimeout);
      bg.querySelectorAll('.loginEmber').forEach((el) => el.remove());
      screen?.querySelectorAll('.loginEmber, .loginWeldFlash').forEach((el) => el.remove());
    };
  }, [bgRef, cardRef, screenRef]);
}

const LoginPage = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { company } = useParams();
  const bgRef = useRef(null);
  const cardRef = useRef(null);
  const screenRef = useRef(null);
  const googleBtnRef = useRef(null);
  const [googleReady, setGoogleReady] = useState(false); // κρύβει το «ή» όταν δεν υπάρχει Google
  useEmberBursts(bgRef, cardRef, screenRef);

  // «Σύνδεση με Google»: όποιος γράφτηκε μέσω Google (Buzz signup) συνδέεται ξανά
  // με ένα tap, από ΕΔΩ — για όλες τις υπηρεσίες (ο προορισμός βγαίνει από το defaultPath).
  // Χρησιμοποιεί το ίδιο endpoint με το signup: υπάρχον identity ⇒ login, τίποτα άλλο.
  const handleGoogleCredential = async (credential) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/public/buzz/google`,
        { credential },
        { withCredentials: true }
      );
      if (data.needBusinessName) {
        // Δεν κάνουμε εγγραφή από τη σελίδα login — μόνο καθαρή είσοδος
        setError(tr('No account is linked to this Google account. Log in with your password, or sign up first.'));
        return;
      }
      const sess = await axios.get(`${config.API_BASE_URL}/users/session`, { withCredentials: true });
      if (!sess.data?.active) {
        setError(tr('Something went wrong — please try again.'));
        return;
      }
      const { data: csrf } = await axios.get(`${config.API_BASE_URL}/csrf-token`, { withCredentials: true });
      axios.defaults.headers.common['X-CSRF-Token'] = csrf.csrfToken;
      sessionStorage.setItem('csrfToken', csrf.csrfToken);
      localStorage.setItem('user', JSON.stringify(sess.data.user));
      await onLogin(sess.data.user);
      navigate('/', { replace: true });
    } catch (err) {
      const code = err?.response?.data?.error;
      setError(
        code === 'ambiguous-email' ? tr('This email is used by multiple accounts — please log in with your password.')
        : code === 'invalid-google-token' ? tr('Google sign-in failed — please try again.')
        : tr('Something went wrong — please try again.')
      );
    } finally {
      setBusy(false);
    }
  };

  // Google Identity Services: φορτώνεται μόνο αν υπάρχει client id στο backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/public/buzz/auth-config`);
        if (cancelled || !data.googleClientId) return;
        const lang = (i18n.resolvedLanguage || i18n.language || 'el').startsWith('el') ? 'el' : 'en';
        const init = () => {
          if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
          window.google.accounts.id.initialize({
            client_id: data.googleClientId,
            callback: (resp) => handleGoogleCredential(resp.credential),
          });
          // Πλάτος ίσο με τη φόρμα (η GIS δέχεται έως 400px) + pill shape ώστε το
          // κουμπί να δένει με τα rounded στοιχεία της κάρτας
          const w = Math.min(400, Math.max(220, googleBtnRef.current.parentElement?.offsetWidth
            || googleBtnRef.current.offsetWidth || 320));
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: 'outline', size: 'large', width: w, shape: 'pill',
            text: 'signin_with', locale: lang, logo_alignment: 'center',
          });
          setGoogleReady(true);
        };
        if (window.google?.accounts?.id) return init();
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.onload = init;
        document.head.appendChild(s);
      } catch { /* χωρίς Google — το κουμπί απλώς δεν εμφανίζεται */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ενεργό κατάστημα: από το URL ή το τελευταίο της συσκευής
  const [activeCompany, setActiveCompany] = useState('');
  const [switching, setSwitching] = useState(false);
  const [companyInput, setCompanyInput] = useState('');
  const [recents, setRecents] = useState(readRecents);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    setReason(q.get('reason') || '');
  }, []);

  useEffect(() => {
    try {
      if (company) {
        const c = company.trim().toLowerCase();
        setActiveCompany(c);
        localStorage.setItem(LAST_COMPANY_KEY, c);
        setSwitching(false);
      } else {
        const prev = localStorage.getItem(LAST_COMPANY_KEY);
        if (prev) setActiveCompany(prev);
      }
    } catch {}
  }, [company]);

  const pretty = (c) => (c ? c[0].toUpperCase() + c.slice(1) : '');

  const chooseCompany = (name) => {
    const c = String(name || '').trim().toLowerCase();
    if (!c) return;
    setError('');
    setActiveCompany(c);
    setCompanyInput('');
    setSwitching(false);
    try { localStorage.setItem(LAST_COMPANY_KEY, c); } catch {}
    // Κράτα URL και κατάστημα συγχρονισμένα όταν ήρθαμε από /login/:company
    if (company && company.trim().toLowerCase() !== c) {
      navigate(`/login/${c}`, { replace: true });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const companyName = (activeCompany || companyInput).trim().toLowerCase();
    if (!companyName) {
      setError(tr('Please enter your company'));
      setSwitching(true);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/users/login/${companyName}`,
        credentials,
        { withCredentials: true } // ensure session cookie on iOS
      );

      const { data } = await axios.get(`${config.API_BASE_URL}/csrf-token`, { withCredentials: true });
      axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
      sessionStorage.setItem('csrfToken', data.csrfToken);

      // Στα «πρόσφατα» πάντα το slug της εταιρείας — αν η σύνδεση έγινε με ΑΦΜ,
      // ο server επιστρέφει το πραγματικό όνομα στο _company_name
      saveRecent(response.data?._company_name || companyName);
      localStorage.setItem("user", JSON.stringify(response.data)); // Store user data
      await onLogin(response.data); // Pass user data to App component
      navigate('/', { replace: true }); // Redirect to home after login
    } catch (err) {
      setError(tr('Invalid username or password'));
    } finally {
      setBusy(false);
    }
  };

  const showSwitchPanel = switching || !activeCompany;
  const recentChoices = recents.filter(c => c !== activeCompany);

  return (
    <div className="loginPage">
      {/* Διακριτικός κινούμενος διάκοσμος — καθαρά διακοσμητικός */}
      <div className="loginBg" aria-hidden="true" ref={bgRef}>
        <span className="loginArc a1" />
        <span className="loginArc a2" />
        <span className="loginArc a3" />
        <span className="loginArc a4" />
        <span className="loginArc a5" />
        <span className="loginArc a6" />
        <span className="loginRing r1" />
        <span className="loginRing r2" />
        <span className="loginGlow g1" />
        <span className="loginGlow g2" />
        {/* Οι σπίθες δημιουργούνται δυναμικά από το useEmberBursts */}
      </div>
      {/* Full-screen στρώμα για τις σπίθες ηλεκτροκόλλησης της φλόγας:
          απλώνονται σε όλη την οθόνη, πάνω από την κάρτα */}
      <div className="loginScreenSparks" aria-hidden="true" ref={screenRef} />
      <div className="loginCard" ref={cardRef}>
        <div className="loginBrand">
          <BrandMark size={64} textPx={30} href="/home" />
        </div>

        {/* Τα spans δίνουν τα πραγματικά όρια των γραμμάτων, ώστε οι σπίθες
            της φλόγας να προσγειώνονται πάνω στο κείμενο με σωστή φυσική */}
        <h1 className="loginTitle">
          <span>{activeCompany ? tr('Welcome back') : tr('Sign in')}</span>
        </h1>
        <p className="loginSubtitle"><span>{tr('Sign in to your account to continue')}</span></p>

        {reason && (
          <div className={`loginAlert ${reason === 'expired' ? 'warn' : 'error'}`}>
            {reason === 'expired'
              ? tr('Your session has expired. Please log in again.')
              : tr('The service is temporarily unavailable. Please try connecting again in a moment.')}
          </div>
        )}
        {error && <div className="loginAlert error">{error}</div>}

        {activeCompany && !showSwitchPanel && (
          <div className="loginCompany">
            <span className="loginCompanyName">
              <span className="ic" aria-hidden="true">🏪</span>
              <span className="nm">{pretty(activeCompany)}</span>
            </span>
            <button
              type="button"
              className="loginCompanySwitch"
              onClick={() => setSwitching(true)}
            >{tr('Change')}</button>
          </div>
        )}

        {showSwitchPanel && (
          <div className="loginSwitchPanel">
            {recentChoices.length > 0 && (
              <>
                <p className="loginSwitchLabel">{tr('Recent on this device')}</p>
                <div className="loginRecentChips">
                  {recentChoices.map((c) => (
                    <button key={c} type="button" className="loginChip" onClick={() => chooseCompany(c)}>
                      {pretty(c)}
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="loginSwitchLabel">{tr('Company')}</p>
            <div className="loginSwitchRow">
              <input
                type="text"
                name="company"
                id="company"
                className="loginInput"
                placeholder={tr('Company name or ΑΦΜ')}
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); chooseCompany(companyInput); }
                }}
                autoComplete="organization"
                autoCapitalize="none"
                autoCorrect="off"
              />
              {(activeCompany || companyInput.trim()) && (
                <button
                  type="button"
                  className="loginSwitchGo"
                  onClick={() => (companyInput.trim() ? chooseCompany(companyInput) : setSwitching(false))}
                >{companyInput.trim() ? tr('OK') : tr('Cancel')}</button>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="on">
          <div className="loginField">
            <label htmlFor="username">{tr('Username')}</label>
            <input
              type="text"
              name="username"
              id="username"
              className="loginInput"
              placeholder={tr('Username')}
              value={credentials.username}
              onChange={handleChange}
              required
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>
          <div className="loginField">
            <label htmlFor="current-password">{tr('Password')}</label>
            <input
              type="password"
              name="password"
              id="current-password"
              className="loginInput"
              placeholder={tr('Password')}
              value={credentials.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className="loginSubmit" disabled={busy}>
            {busy ? tr('Signing in…') : tr('Login')}
          </button>
          {/* Σύνδεση με Google — divider + κουμπί μόνο όταν το backend έχει client id */}
          {googleReady && <div className="loginDivider"><span>{tr('or')}</span></div>}
          <div className="loginGoogleWrap"><div ref={googleBtnRef} /></div>
          <div className="loginFooter">
            <a href="/forgot-password">{tr('Forgot your password?')}</a>
          </div>
          <div className="loginFooter" style={{ fontSize: 11.5, opacity: 0.75, marginTop: 6 }}>
            <a href="/api/public/legal/terms" target="_blank" rel="noreferrer">{tr('Terms')}</a>
            {' · '}
            <a href="/api/public/legal/privacy" target="_blank" rel="noreferrer">{tr('Privacy')}</a>
            {' · '}
            <a href="/api/public/legal/cookies" target="_blank" rel="noreferrer">Cookies</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
